import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

function CreateCompanyModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    billingLimits: {
      maxSeats: 5,
      maxStorageGB: 10,
      maxAITokensPerMonth: 100000
    }
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminApi.createCompany(formData);
      toast.success('Company created successfully!');
      onSuccess();
      onClose();
      setFormData({
        name: '',
        industry: '',
        billingLimits: { maxSeats: 5, maxStorageGB: 10, maxAITokensPerMonth: 100000 }
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Create New Company
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                className="input"
                placeholder="Acme Corporation"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Industry
              </label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData(p => ({ ...p, industry: e.target.value }))}
                className="input"
              >
                <option value="">Select Industry</option>
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="education">Education</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="border-t border-surface-700 pt-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3">Billing Limits</h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Max Seats</label>
                  <input
                    type="number"
                    value={formData.billingLimits.maxSeats}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      billingLimits: { ...p.billingLimits, maxSeats: parseInt(e.target.value) }
                    }))}
                    className="input"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Storage (GB)</label>
                  <input
                    type="number"
                    value={formData.billingLimits.maxStorageGB}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      billingLimits: { ...p.billingLimits, maxStorageGB: parseInt(e.target.value) }
                    }))}
                    className="input"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-surface-400 mb-1">AI Tokens/Mo</label>
                  <input
                    type="number"
                    value={formData.billingLimits.maxAITokensPerMonth}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      billingLimits: { ...p.billingLimits, maxAITokensPerMonth: parseInt(e.target.value) }
                    }))}
                    className="input"
                    min="0"
                    step="10000"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CompanyCard({ company, onToggleStatus }) {
  return (
    <div className="card hover:border-surface-600 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
            <BuildingOfficeIcon className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-surface-100">{company.name}</h3>
            <p className="text-sm text-surface-400">{company.industry || 'No industry set'}</p>
          </div>
        </div>
        <span className={company.isActive ? 'badge-success' : 'badge-danger'}>
          {company.isActive ? (
            <><CheckCircleIcon className="w-3 h-3 mr-1" /> Active</>
          ) : (
            <><XCircleIcon className="w-3 h-3 mr-1" /> Inactive</>
          )}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <UsersIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">{company.userCount || 0}</p>
          <p className="text-xs text-surface-400">Users</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <ChartBarSquareIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">{company.dashboardCount || 0}</p>
          <p className="text-xs text-surface-400">Dashboards</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <CircleStackIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">
            {company.currentUsage?.storageUsedGB?.toFixed(1) || 0}
          </p>
          <p className="text-xs text-surface-400">GB Used</p>
        </div>
      </div>
      
      {/* Limits */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-surface-400">Seat Limit</span>
          <span className="text-surface-300">
            {company.currentUsage?.seats || 0} / {company.billingLimits?.maxSeats || 5}
          </span>
        </div>
        <div className="w-full h-1.5 bg-surface-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary-500 rounded-full"
            style={{ 
              width: `${Math.min(100, ((company.currentUsage?.seats || 0) / (company.billingLimits?.maxSeats || 5)) * 100)}%`
            }}
          />
        </div>
      </div>
      
      <div className="flex gap-2 pt-4 border-t border-surface-700">
        <button
          onClick={() => onToggleStatus(company)}
          className={company.isActive ? 'btn-ghost btn-sm text-red-400 flex-1' : 'btn-ghost btn-sm text-emerald-400 flex-1'}
        >
          {company.isActive ? 'Deactivate' : 'Activate'}
        </button>
        <button className="btn-secondary btn-sm flex-1">
          View Details
        </button>
      </div>
    </div>
  );
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  useEffect(() => {
    loadCompanies();
  }, [pagination.page, filters]);
  
  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCompanies({
        page: pagination.page,
        limit: 12,
        ...filters
      });
      setCompanies(response.data.companies);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCompanyStatus = async (company) => {
    try {
      await adminApi.updateCompany(company._id, { isActive: !company.isActive });
      toast.success(`Company ${company.isActive ? 'deactivated' : 'activated'}`);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update company');
    }
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Company Management</h1>
          <p className="text-surface-400 mt-1">Manage all companies on the platform</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Add Company
        </button>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Search companies..."
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
              className="input pl-10"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
            className="input"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      
      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : companies.length === 0 ? (
        <div className="card text-center py-12">
          <BuildingOfficeIcon className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-300 mb-2">No companies found</h3>
          <p className="text-surface-400 mb-6">Get started by creating your first company</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
            Add Company
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map(company => (
            <CompanyCard 
              key={company._id} 
              company={company}
              onToggleStatus={toggleCompanyStatus}
            />
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {companies.length} of {pagination.total} companies
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      <CreateCompanyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadCompanies}
      />
    </div>
  );
}

